import {
  configureStore,
  createListenerMiddleware,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  actionTypes,
  router5Middleware,
  router5Reducer,
  actions as routerActions,
} from "redux-router5";
import createRouter, { type State as RouterState } from "router5";
import browserPluginFactory from "router5-plugin-browser";
import { UNPAGED_LIMIT } from "../common/constants/constants";
import {
  arrayToObject,
  resolveMissingEntityMap,
  responseUnwrap,
} from "../common/functions";
import { collectReferencedIds } from "./ticket/worklog/workLogFormatter";
import { publicRoutes, routeNames, routes } from "./common/routes";
import { appFeedbackSlice } from "../features/app-feedback/appFeedbackSlice";
import bladeReducer, { bladeActions } from "../features/blade/bladeSlice";
import dashboardReducer, { dashboardActions } from "../features/dashboard/dashboardSlice";
import clientReducer, { clientActions } from "../features/client/clientSlice";
import intlSliceReducer from "../features/intl/intlSlice";
import locationReducer, { locationActions } from "../features/location/locationSlice";
import materialsReducer, { materialsActions } from "../features/materials/materialsSlice";
import notificationReducer, {
  notificationActions,
} from "../features/notification/notificationSlice";
import projectReducer, { projectActions } from "../features/project/projectSlice";
import servicesReducer, { servicesActions } from "../features/services/servicesSlice";
import ticketDataReducer, {
  ticketDataActions,
} from "../features/ticket-data/ticketDataSlice";
import ticketReducer, {
  ticketActions,
  type SideloadedTicketInfo,
  type TicketDateEditPayload,
} from "../features/ticket/ticketSlice";
import tokenReducer from "../features/token/tokenSlice";
import serviceCounterReducer from "../features/service-count/serviceCountSlice";
import turbineReducer, {
  turbineActions,
  type SideloadedTurbineInfo,
} from "../features/turbine/turbineSlice";
import userManagementReducer, {
  userManagementActions,
} from "../features/user-management/userManagementSlice";
import userSliceReducer, { userActions } from "../features/user/userSlice";
import type {
  ApiV1ManagementClientsTurbinesListSideloadEnum,
  ApiV1ManagementTechTurbinesListSideloadEnum,
  ApiV1ManagementTicketListSideloadEnum,
  ApiV1ManagementTurbinesListSideloadEnum,
  Blade,
  Client,
  Location,
  Materials,
  ModelFile,
  Notification as DelayNotification,
  NotificationList,
  PaginatedClientList,
  PaginatedMaterialsList,
  PaginatedProjectList,
  PaginatedticketResultsSideloadList,
  PaginatedServicesList,
  PaginatedUserList,
  Project,
  ProjectDetailed,
  Services,
  Ticket,
  TicketData,
  TicketRetrieve,
  TicketStandbyPeriod,
  TimeTracking,
  Turbine,
  TurbineRetrieve,
  User,
  UserGroup,
  UserProfile,
  WorkLog,
} from "./generated-axios-client";
import type { Err } from "../types/err";
import type { NavigatePayload } from "../types/router";
import { client, silentClient } from "./api-client";
import {
  buildAssigneeById,
  buildClientById,
  buildServiceById,
  filterActiveTechnicians,
  getTechnicianGroupId,
} from "./dashboard/dashboard.utils";
export type RootState = ReturnType<ReturnType<typeof confStore>["getState"]>;

export type AppDispatch = typeof store.dispatch;

type NavigateAction = PayloadAction<NavigatePayload>;
const isNavigateAction = (
  action: NavigateAction,
  nameMatcher: string | ((name: string) => boolean),
  _args?: object
) => {
  if (nameMatcher instanceof Function) {
    return (
      action.type === actionTypes.TRANSITION_SUCCESS &&
      nameMatcher(action.payload.route.name)
    );
  }
  return (
    action.type === actionTypes.TRANSITION_SUCCESS &&
    action.payload.route.name === nameMatcher
  );
};

function handleErrorNotification(state: RootState, error: Err) {
  const infoFunc = state.appFeedback.notification?.info;
  const intl = state.intl.datetimeIntl;
  if ("error" in error) {
    infoFunc?.({
      title: intl.formatMessage({ id: `${error?.data?.code}` }),
    });
  }
}

const VALID_PAGE_SIZES = [10, 25, 50, 100] as const;

function getPerPage(params: Record<string, unknown>): number | undefined {
  const fromRoute = params?.per_page;
  if (fromRoute) {
    const n = Number(fromRoute);
    if ((VALID_PAGE_SIZES as readonly number[]).includes(n)) return n;
  }
  const stored = localStorage.getItem("per_page");
  if (stored) {
    const n = parseInt(stored, 10);
    if ((VALID_PAGE_SIZES as readonly number[]).includes(n)) return n;
  }
  return undefined;
}

function fetchUserList(params: RouterState["params"] = {}) {
  return client.apiV1ManagementUserManagementList(
    params.page,
    params.email,
    params.groups__name,
    params.is_active,
    params.sort,
    getPerPage(params),
    params.search,
    params.technician_level
  );
}

async function fetchAndStoreTicketData(
  dispatch: AppDispatch,
  filters: Partial<Record<string, string>> = {}
) {
  const response = await client.apiV1ManagementWorkOrdersMetaRetrieve(
    filters.client,
    filters.location,
    filters.turbine
  );
  const unwrappedResponse = responseUnwrap<TicketData>(response);

  if (!("error" in unwrappedResponse)) {
    dispatch(ticketDataActions.addTicketData(unwrappedResponse));
    // Clear stale blades when switching client — they belong to the old turbine
    if (filters.client && !filters.turbine && !filters.location) {
      dispatch(ticketDataActions.clearTicketDataBlades());
    }
  }
}

export const listener = createListenerMiddleware();

//fetch ticket data for ticket creation
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketDataActions.fetchTicketData,
  effect: async (action, { dispatch }) => {
    await fetchAndStoreTicketData(dispatch, action.payload);
  },
});

// add or update ticket
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.addOrUpdateTicket,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const addTicketResponse = await client.apiV1ManagementTicketCreate(action.payload);
      const unwrappedAddTicketResponse = responseUnwrap<Ticket>(addTicketResponse);

      if (!("error" in unwrappedAddTicketResponse)) {
        action.payload = unwrappedAddTicketResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_ticket_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.TICKET_LIST));
      }
    } else {
      const updateTicketResponse = await client.apiV1ManagementTicketUpdate(
        action.payload.id,
        action.payload
      );
      const unwrappedUpdateTicketResponse = responseUnwrap<Ticket>(updateTicketResponse);
      if (!("error" in unwrappedUpdateTicketResponse)) {
        action.payload = unwrappedUpdateTicketResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_ticket_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.TICKET_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

//fetch ticket on edit or view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW),
  effect: async (action, { dispatch }) => {
    dispatch(ticketActions.removeTicket());
    const navigateAction = action as NavigateAction;
    const ticketId = navigateAction.payload.route.params.id;

    const getTicketResponse = await client.apiV1ManagementTicketRetrieve(ticketId);

    const unwrappedResponse = responseUnwrap<TicketRetrieve>(getTicketResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.storeTicket(unwrappedResponse));
    }

    if (isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW)) {
      // WO History merges worklog, standby and rejection entries into one stream and
      // sorts it newest-first in the browser. That is only correct because the requests
      // below are unpaged (UNPAGED_LIMIT): the worklog endpoint has no .order_by(), so a
      // real page size would hand back an arbitrary SUBSET and the merged view would
      // present a confidently wrong order with no visible error. The standby periods are
      // unpaged for the same reason — they are matched one-to-one against the resume rows,
      // and a missing period would leave its row without a duration. Lowering the limit or
      // introducing pagination here requires ordering the worklog queryset server-side.
      const [sessionsRes, worklogRes, delaysRes, rejectionsRes, standbyPeriodsRes] =
        await Promise.all([
          client.apiV1ManagementTimeTrackingList(
            undefined,
            undefined,
            "-started_at",
            UNPAGED_LIMIT,
            ticketId
          ),
          client.apiV1ManagementWorklogList(ticketId, undefined, UNPAGED_LIMIT),
          client.apiV1ManagementTicketDelaysList(
            undefined,
            "-created_at",
            UNPAGED_LIMIT,
            undefined,
            undefined,
            undefined,
            ticketId
          ),
          client.apiV1ManagementTicketRejectionsList(
            undefined,
            "-created_at",
            UNPAGED_LIMIT,
            undefined,
            undefined,
            undefined,
            ticketId
          ),
          client.apiV1ManagementTicketStandbyPeriodsList(
            undefined,
            "started_at",
            UNPAGED_LIMIT,
            ticketId
          ),
        ]);
      dispatch(
        ticketActions.storeTimeSessions(
          (sessionsRes.data?.results ?? []) as TimeTracking[]
        )
      );
      const worklogEntries = (worklogRes.data?.results ?? []) as WorkLog[];
      dispatch(ticketActions.storeWorklogEntries(worklogEntries));
      dispatch(
        ticketActions.storeTicketDelays(
          (delaysRes.data?.results ?? []) as DelayNotification[]
        )
      );
      dispatch(
        ticketActions.storeTicketRejections(
          (rejectionsRes.data?.results ?? []) as DelayNotification[]
        )
      );
      dispatch(
        ticketActions.storeStandbyPeriods(
          (standbyPeriodsRes.data?.results ?? []) as TicketStandbyPeriod[]
        )
      );

      // Resolve location/turbine/client/project names referenced in this ticket's
      // worklog history — seeded for free from the ticket's own nested objects,
      // only fetching ids that differ from the ticket's current values (i.e. past
      // reassignments), never the full catalog.
      const currentTicket = !("error" in unwrappedResponse) ? unwrappedResponse : null;
      const seedLocationMap: Record<string, string> = currentTicket?.location
        ? { [currentTicket.location.id!]: currentTicket.location.name ?? "" }
        : {};
      const seedTurbineMap: Record<string, string> = currentTicket?.turbine
        ? { [currentTicket.turbine.id!]: currentTicket.turbine.serial_number ?? "" }
        : {};
      const seedClientMap: Record<string, string> = currentTicket?.client
        ? { [currentTicket.client.id!]: currentTicket.client.legal_name ?? "" }
        : {};
      const seedProjectMap: Record<string, string> = currentTicket?.project
        ? { [currentTicket.project.id!]: currentTicket.project.name ?? "" }
        : {};

      const referenced = collectReferencedIds(worklogEntries, [
        "location",
        "turbine",
        "client",
        "project",
      ]);

      const [locationMap, turbineMap, clientMap, projectMap] = await Promise.all([
        resolveMissingEntityMap<Location>(
          referenced.location,
          seedLocationMap,
          (id) => client.apiV1ManagementLocationsRetrieve(id),
          (l) => l.name ?? ""
        ),
        resolveMissingEntityMap<TurbineRetrieve>(
          referenced.turbine,
          seedTurbineMap,
          (id) => client.apiV1ManagementTurbinesRetrieve(id),
          (t) => t.serial_number ?? ""
        ),
        resolveMissingEntityMap<Client>(
          referenced.client,
          seedClientMap,
          (id) => client.apiV1ManagementClientsRetrieve(id),
          (c) => c.legal_name ?? ""
        ),
        resolveMissingEntityMap<ProjectDetailed>(
          referenced.project,
          seedProjectMap,
          (id) => client.apiV1ManagementProjectsRetrieve(id),
          (p) => p.name ?? ""
        ),
      ]);
      dispatch(
        ticketActions.storeWorklogEntityMaps({
          location: locationMap,
          turbine: turbineMap,
          client: clientMap,
          project: projectMap,
        })
      );
    }
  },
});

//fetch all ticket data when navigating to ticket edit so dropdowns are fully populated
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_EDIT),
  effect: async (_action, { dispatch }) => {
    dispatch(ticketDataActions.fetchTicketData({}));
  },
});

//remove ticket from state and add ticket data to state
//when navigating to ticket creation
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_CREATE) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_CREATE) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_EDIT),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const params = navigateAction.payload.route.params;
    dispatch(ticketActions.removeTicket());

    // The unfiltered fetch has to come first and finish before the prefill one:
    // a client+turbine response only narrows turbines, blades, locations and
    // projects, so on its own it never delivers the clients and technicians the
    // create form waits for before it renders. Doing it the other way round would
    // also let the unfiltered response wipe the prefilled turbine's blades.
    await fetchAndStoreTicketData(dispatch);

    const prefillFilters = {
      client: params?.client,
      turbine: params?.turbine,
    };
    if (prefillFilters.client || prefillFilters.turbine) {
      await fetchAndStoreTicketData(dispatch, prefillFilters);
    }
  },
});

//load client tickets when navigating to client view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const response = await client.apiV1ManagementTicketList(
      undefined,
      navigateAction.payload.route.params.id,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      [
        "assignees,created_by,turbine,location, client, coordinators" as ApiV1ManagementTicketListSideloadEnum,
      ],
      undefined,
      undefined
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.addTicketList(unwrappedResponse));

      const sideload = {} as SideloadedTicketInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(ticketActions.addTicketSideload(sideload));
    }
  },
});

// save ticket items (add or update tasks)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.saveTicketItems,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { ticketId, items } = action.payload;

    const response = await client.apiV1ManagementTicketPartialUpdate(ticketId, {
      items,
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_update_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(ticketId);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// start work order
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.startWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;

    const response =
      await client.apiV1ManagementTechnicianWorkOrdersStartPartialUpdate(id);
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_start_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// end work order (Done)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.endWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, summaryText } = action.payload;

    const response = await client.apiV1ManagementTechnicianWorkOrdersEndPartialUpdate(
      id,
      {
        summary_text: summaryText,
      }
    );
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_end_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// confirm work order as done (coordinator review decision)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.confirmWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;

    // Dedicated approve endpoint — server sets status=DONE and resolution=APPROVED,
    // so no body is sent and neither field is set client-side.
    const response = await client.apiV1ManagementTicketTicketApprovePartialUpdate(id);
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_confirm_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
    // A non-200 (e.g. 400 ticket_must_be_in_review from a stale tab) is surfaced
    // by the global axios error interceptor — no per-listener handling needed.
  },
});

// reject work order back to in progress (coordinator review decision)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.rejectWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, message } = action.payload;

    // Dedicated reject endpoint — server sets status=IN_PROGRESS and
    // resolution=REJECTED. The optional message becomes the rejection
    // notification text the technician reads.
    const response = await client.apiV1ManagementTicketTicketRejectPartialUpdate(id, {
      message,
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_reject_success" }),
      });
      const [ticketResponse, rejectionsRes] = await Promise.all([
        client.apiV1ManagementTicketRetrieve(id),
        client.apiV1ManagementTicketRejectionsList(
          undefined,
          "-created_at",
          UNPAGED_LIMIT,
          undefined,
          undefined,
          undefined,
          id
        ),
      ]);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
      dispatch(
        ticketActions.storeTicketRejections(
          (rejectionsRes.data?.results ?? []) as DelayNotification[]
        )
      );
    }
    // A non-200 (e.g. 400 ticket_must_be_in_review from a stale tab) is surfaced
    // by the global axios error interceptor — no per-listener handling needed.
  },
});

// delay work order
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.delayWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, message, standby_to, standby_from } = action.payload;

    const response =
      await client.apiV1ManagementTechnicianWorkOrdersReportDelayPartialUpdate(id, {
        message,
        standby_to: standby_to ?? null,
        standby_from: standby_from ?? null,
      });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_delay_success" }),
      });
      const [ticketResponse, delaysRes] = await Promise.all([
        client.apiV1ManagementTicketRetrieve(id),
        client.apiV1ManagementTicketDelaysList(
          undefined,
          "-created_at",
          UNPAGED_LIMIT,
          undefined,
          undefined,
          undefined,
          id
        ),
      ]);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
      dispatch(
        ticketActions.storeTicketDelays(
          (delaysRes.data?.results ?? []) as DelayNotification[]
        )
      );
    }
  },
});

// refresh current ticket + time sessions (dispatched after a time entry edit)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.refreshCurrentTicket,
  effect: async (action, { dispatch }) => {
    const id = action.payload;
    const [ticketResponse, sessionsRes] = await Promise.all([
      client.apiV1ManagementTicketRetrieve(id),
      client.apiV1ManagementTimeTrackingList(
        undefined,
        undefined,
        "-started_at",
        UNPAGED_LIMIT,
        id
      ),
    ]);
    const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.storeTicket(unwrapped));
    }
    dispatch(
      ticketActions.storeTimeSessions((sessionsRes.data?.results ?? []) as TimeTracking[])
    );
  },
});

// edit the times of a check-in session or a manual task time log
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.editTimeEntry,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { entryId, ticketId, started_at, ended_at } = action.payload;

    // The body is the eighth positional argument; the six query params in between
    // are list filters the generated client attaches to every action on this
    // viewset and are unused here.
    const response = await client.apiV1ManagementTimeTrackingEditTimePartialUpdate(
      entryId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { started_at, ended_at }
    );
    if (response.status !== 200) {
      dispatch(ticketActions.setEditTimeEntryStatus("failed"));
      return;
    }
    dispatch(ticketActions.setEditTimeEntryStatus("succeeded"));
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_time_entry_edit_success" }),
    });
    // The response carries the edited cohort, but total_time is derived from it and
    // lives on the ticket, so the ticket has to be re-read either way.
    dispatch(ticketActions.refreshCurrentTicket(ticketId));
  },
});

// re-read the ticket plus the two lists WO History is built from
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.refreshTicketHistory,
  effect: async (action, { dispatch }) => {
    const id = action.payload;
    const [ticketResponse, worklogRes, standbyPeriodsRes] = await Promise.all([
      client.apiV1ManagementTicketRetrieve(id),
      client.apiV1ManagementWorklogList(id, undefined, UNPAGED_LIMIT),
      client.apiV1ManagementTicketStandbyPeriodsList(
        undefined,
        "started_at",
        UNPAGED_LIMIT,
        id
      ),
    ]);
    const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.storeTicket(unwrapped));
    }
    // worklogEntityMaps is deliberately left as the navigation listener built it: a
    // standby period edit logs only started_at/ended_at/message, which reference no
    // location, turbine, client or project the maps would need to resolve.
    dispatch(
      ticketActions.storeWorklogEntries((worklogRes.data?.results ?? []) as WorkLog[])
    );
    dispatch(
      ticketActions.storeStandbyPeriods(
        (standbyPeriodsRes.data?.results ?? []) as TicketStandbyPeriod[]
      )
    );
  },
});

// correct the start, end or reason of a recorded standby period
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.editStandbyPeriod,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { periodId, ticketId, started_at, ended_at, message } = action.payload;

    // Only these three fields are sent. PatchedTicketStandbyPeriod advertises the whole
    // model because partial_update has no request-schema override, but the API's edit
    // serializer accepts nothing else.
    const response = await client.apiV1ManagementTicketStandbyPeriodsPartialUpdate(
      periodId,
      { started_at, ended_at, message }
    );
    if (response.status !== 200) {
      dispatch(ticketActions.setEditStandbyPeriodStatus("failed"));
      return;
    }
    dispatch(ticketActions.setEditStandbyPeriodStatus("succeeded"));
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_standby_period_edit_success" }),
    });
    // Total Standby Time lives on the ticket, the duration line comes from the periods
    // list, and the edit writes an audit row into the worklog — all three have moved.
    dispatch(ticketActions.refreshTicketHistory(ticketId));
  },
});

// set work order to In Review
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.reviewWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;

    const response = await client.apiV1ManagementTechnicianWorkOrdersPartialUpdate(id, {
      status: "IN_REVIEW",
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_review_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

//delete ticket
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.deleteTicket,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementTicketDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_delete_success" }),
      });

      const response = await client.apiV1ManagementTicketList(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        [
          "assignees,created_by,turbine,location, client, coordinators" as ApiV1ManagementTicketListSideloadEnum,
        ],
        undefined,
        undefined
      );

      const unwrappedResponse = responseUnwrap(response);
      if (!("error" in unwrappedResponse)) {
        dispatch(ticketActions.addTicketList(unwrappedResponse));

        const sideload = {} as SideloadedTicketInfo;
        for (const [key, value] of Object.entries(unwrappedResponse.results)) {
          sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
        }
        dispatch(ticketActions.addTicketSideload(sideload));
      }
    }
  },
});

// update current user, statuses, services, user list on transition
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, (name) => !(name in publicRoutes)),
  effect: async (_action, { dispatch, getState }) => {
    const state = getState();

    // load current user
    if (state.userstate.user === undefined) {
      const userResponse = await client.apiV1ManagementMeRetrieve();
      const unwrappedUserResponse = responseUnwrap(userResponse);
      if (!("error" in unwrappedUserResponse)) {
        dispatch(userActions.getUser(unwrappedUserResponse));
      }
    }

    // load user list
    if (
      state.userstate.list.results === undefined ||
      state.userstate.list.results.length === 0
    ) {
      const userListResponse = await client.apiV1ManagementUsersList(
        1,
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedUserListResponse = responseUnwrap(userListResponse);
      if (!("error" in unwrappedUserListResponse)) {
        dispatch(userActions.getUserList(unwrappedUserListResponse));
      }
    }

    //load services
    if (state.ticket.services === null) {
      const serviceResponse = await client.apiV1ManagementServicesList(
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedServiceResponse = responseUnwrap(serviceResponse);
      if (!("error" in unwrappedServiceResponse)) {
        dispatch(ticketActions.addService(unwrappedServiceResponse.results));

        const serviceObj = arrayToObject(unwrappedServiceResponse.results, "id");
        dispatch(ticketActions.addServiceObj(serviceObj));
      }

      //load statuses
      if (state.ticket.statuses === null) {
        const statusResponse = await client.apiV1ManagementMetaTicketStatusesList();
        const unwrappedStatusResponse = responseUnwrap(statusResponse);
        if (!("error" in unwrappedStatusResponse)) {
          dispatch(ticketActions.addStatus(unwrappedStatusResponse));

          const statusObj = arrayToObject(unwrappedStatusResponse, "value");
          dispatch(ticketActions.addStatusObj(statusObj));
        }
      }
    }

    // load materials
    // if (
    // 	state.materials.list.results === undefined ||
    // 	state.materials.list.results.length === 0
    // ) {
    // 	const materialsResponse = await client.apiV1MaterialsList(
    // 		undefined,
    // 		undefined,
    // 		undefined,
    // 		undefined,
    // 		UNPAGED_LIMIT,
    // 	);
    // 	const unwrappedMaterials =
    // 		responseUnwrap<PaginatedMaterialsList>(materialsResponse);
    // 	if (!("error" in unwrappedMaterials)) {
    // 		dispatch(materialsActions.addMaterialList(unwrappedMaterials));
    // 	}
    // }
  },
});

//loads ticket list when navigating to ticket list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_LIST),
  effect: async (action, { dispatch, getState }) => {
    dispatch(ticketDataActions.clearTicketData());
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementTicketList(
      navigateAction.payload.route.params.assignees,
      navigateAction.payload.route.params.client,
      navigateAction.payload.route.params.coordinators,
      navigateAction.payload.route.params.created_by,
      navigateAction.payload.route.params.page,
      undefined,
      navigateAction.payload.route.params.sort,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.project,
      navigateAction.payload.route.params.report_done,
      navigateAction.payload.route.params.search,
      navigateAction.payload.route.params.service,
      [
        "assignees,created_by,turbine,client,location,coordinators" as ApiV1ManagementTicketListSideloadEnum,
      ],
      navigateAction.payload.route.params.status,
      navigateAction.payload.route.params.turbine
    );

    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.addTicketList(unwrappedResponse));

      const sideload = {} as SideloadedTicketInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(ticketActions.addTicketSideload(sideload));
    }
  },
});

//fetch client list when navigating to client list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementClientsList(
      navigateAction.payload.route.params.page,
      navigateAction.payload.route.params.legal_name,
      navigateAction.payload.route.params.sort,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.search,
      navigateAction.payload.route.params.tax_id
    );

    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(clientActions.addClientList(unwrappedResponse));
    }
  },
});

//fetch data when navigating to turbine create page, ticket list page, turbine list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_CREATE) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_LIST) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_LIST) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_EDIT),
  effect: async (_action, { dispatch, getState }) => {
    const state = getState();
    if (
      state.ticketData.clients === undefined ||
      state.ticketData.turbines === undefined ||
      state.ticketData.technicians === undefined ||
      state.ticketData.materials === undefined
    ) {
      const response = await client.apiV1ManagementWorkOrdersMetaRetrieve();
      const unwrappedResponse = responseUnwrap<TicketData>(response);

      if (!("error" in unwrappedResponse)) {
        dispatch(ticketDataActions.addTicketData(unwrappedResponse));
      }
    }
  },
});

//delete client
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: clientActions.deleteClient,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementClientsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_client_delete_success" }),
      });

      const response = await client.apiV1ManagementClientsList();

      const unwrappedResponse = responseUnwrap(response);
      if (!("error" in unwrappedResponse)) {
        dispatch(clientActions.addClientList(unwrappedResponse));
      }
    }
  },
});

// add or update client
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: clientActions.addOrUpdateClient,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const addClientResponse = await client.apiV1ManagementClientsCreate(action.payload);
      const unwrappedAddClientResponse = responseUnwrap<Client>(addClientResponse);

      if (!("error" in unwrappedAddClientResponse)) {
        action.payload = unwrappedAddClientResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_client_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.CLIENT_LIST));
      }
    } else {
      const updateClientResponse = await client.apiV1ManagementClientsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrappedUpdateClientResponse = responseUnwrap<Client>(updateClientResponse);
      if (!("error" in unwrappedUpdateClientResponse)) {
        action.payload = unwrappedUpdateClientResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_client_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.CLIENT_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

//fetch client on edit or view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const clientId = navigateAction.payload.route.params.id;
    dispatch(clientActions.removeClient());

    const getTicketResponse = await client.apiV1ManagementClientsRetrieve(clientId);

    const unwrappedResponse = responseUnwrap<Client>(getTicketResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(clientActions.storeClient(unwrappedResponse));
    }
  },
});

//fetch client turbines on client view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const getTicketResponse = await client.apiV1ManagementClientsTurbinesList(
      navigateAction.payload.route.params.id,
      undefined,
      UNPAGED_LIMIT,
      ["client, location" as ApiV1ManagementClientsTurbinesListSideloadEnum]
    );

    const unwrappedResponse = responseUnwrap(getTicketResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.addTurbineList(unwrappedResponse));

      const sideload = {} as SideloadedTurbineInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(turbineActions.addTurbineSideload(sideload));
    }
  },
});

//fetch client projects on client view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const projectResponse = await client.apiV1ManagementProjectsList(
      navigateAction.payload.route.params.id
    );
    const unwrappedResponse = responseUnwrap<PaginatedProjectList>(projectResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(projectActions.storeClientProjects(unwrappedResponse.results));
    }
  },
});

//fetch turbine specific work orders
listener.startListening.withTypes<AppDispatch>()({
  actionCreator: turbineActions.getTurbineWorkOrders,
  effect: async (action, { dispatch }) => {
    const getWorkOrdersResponse = await client.apiV1ManagementTurbinesWorkOrdersList(
      action.payload,
      undefined,
      undefined,
      [
        "assignees,created_by,turbine,client,location,coordinators" as ApiV1ManagementTicketListSideloadEnum,
      ]
    );
    const unwrappedResponse = responseUnwrap(getWorkOrdersResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.addTicketList(unwrappedResponse));

      const sideload = {} as SideloadedTicketInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(ticketActions.addTicketSideload(sideload));
    }
  },
});

//remove client from state when navigating to client creation
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(clientActions.removeClient());
  },
});

// add or update turbine
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: turbineActions.addOrUpdateTurbine,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const addTurbineResponse = await client.apiV1ManagementTurbinesCreate(
        action.payload
      );
      const unwrappedAddTurbineResponse = responseUnwrap<Turbine>(addTurbineResponse);

      if (!("error" in unwrappedAddTurbineResponse)) {
        action.payload = unwrappedAddTurbineResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_turbine_create_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.TURBINE_VIEW, {
            id: unwrappedAddTurbineResponse.id,
          })
        );
      }
    } else {
      const updateTurbineResponse = await client.apiV1ManagementTurbinesPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrappedUpdateTurbineResponse =
        responseUnwrap<Turbine>(updateTurbineResponse);
      if (!("error" in unwrappedUpdateTurbineResponse)) {
        action.payload = unwrappedUpdateTurbineResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_turbine_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.TURBINE_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// fetch turbine list when navigating to turbine list page
// and when creating tickets
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_LIST) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_CREATE),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const isTurbineList = isNavigateAction(navigateAction, routeNames.TURBINE_LIST);
    const response = await client.apiV1ManagementTurbinesList(
      navigateAction.payload.route.params.client,
      navigateAction.payload.route.params.page,
      navigateAction.payload.route.params.location,
      navigateAction.payload.route.params.sort,
      isTurbineList ? getPerPage(navigateAction.payload.route.params) : undefined,
      navigateAction.payload.route.params.search,
      navigateAction.payload.route.params.serial_number,
      ["client,location" as ApiV1ManagementTurbinesListSideloadEnum]
    );

    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.addTurbineList(unwrappedResponse));

      const sideload = {} as SideloadedTurbineInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(turbineActions.addTurbineSideload(sideload));
    }
  },
});

//fetch turbine on view and edit actions
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(turbineActions.clearTurbines());
    const getTicketResponse = await client.apiV1ManagementTurbinesRetrieve(
      navigateAction.payload.route.params.id
    );

    const unwrappedResponse = responseUnwrap<TurbineRetrieve>(getTicketResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.storeTurbine(unwrappedResponse));
    }
  },
});

//remove turbine from state when navigating to turbine creation
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_CREATE),
  effect: async (action, { dispatch }) => {
    dispatch(turbineActions.removeTurbine(action.payload));
  },
});

//delete turbine
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: turbineActions.deleteTurbine,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementTurbinesDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_turbine_delete_success",
        }),
      });

      const response = await client.apiV1ManagementTurbinesList(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        ["client,location" as ApiV1ManagementTurbinesListSideloadEnum]
      );

      const unwrappedResponse = responseUnwrap(response);
      if (!("error" in unwrappedResponse)) {
        dispatch(turbineActions.addTurbineList(unwrappedResponse));

        const sideload = {} as SideloadedTurbineInfo;
        for (const [key, value] of Object.entries(unwrappedResponse.results)) {
          sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
        }
        dispatch(turbineActions.addTurbineSideload(sideload));
      }
    }
  },
});

//fetch turbine blades on turbine view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const bladeResponse = await client.apiV1ManagementTurbinesBladesList(
      navigateAction.payload.route.params.id
    );

    const unwrappedResponse = responseUnwrap(bladeResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(bladeActions.addBladeList(unwrappedResponse));
    }
  },
});

//fetch ticket files on ticket view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const ticketId = navigateAction.payload.route.params.id;
    const response = await client.apiV1ManagementFilesList(
      undefined,
      undefined,
      ticketId,
      undefined,
      undefined,
      UNPAGED_LIMIT
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.storeTicketFiles(unwrappedResponse.results as ModelFile[]));
    }
  },
});

//update blade
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: bladeActions.bladeEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    if (action.payload.id) {
      const updateBladeResponse = await client.apiV1ManagementBladesPartialUpdate(
        action.payload.id,
        action.payload.data
      );
      const unwrappedResponse = responseUnwrap<Blade>(updateBladeResponse);
      if (!("error" in unwrappedResponse) && unwrappedResponse.turbine) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_blade_update_success",
          }),
        });

        const bladeResponse = await client.apiV1ManagementTurbinesBladesList(
          unwrappedResponse.turbine
        );
        const unwrappedBladeResponse = responseUnwrap(bladeResponse);
        if (!("error" in unwrappedBladeResponse)) {
          dispatch(bladeActions.addBladeList(unwrappedBladeResponse));
        }
      }
    }
  },
});

// inline date edit from Work Orders list
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.ticketDateEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, field, value } = action.payload as TicketDateEditPayload;

    const response = await client.apiV1ManagementTicketPartialUpdate(id, {
      [field]: value,
    });
    const unwrapped = responseUnwrap<Ticket>(response);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.updateTicketDateInList({ id, field, value }));
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_update_success" }),
      });
    } else {
      handleErrorNotification(state, unwrapped);
    }
  },
});

// fetch location list when navigating to location list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementLocationsList(
      navigateAction.payload.route.params.page,
      navigateAction.payload.route.params.sort,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.search
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(locationActions.addLocationList(unwrappedResponse));
    }
  },
});

// fetch location on edit or view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(locationActions.clearLocation());
    const response = await client.apiV1ManagementLocationsRetrieve(
      navigateAction.payload.route.params.id
    );
    const unwrappedResponse = responseUnwrap<Location>(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(locationActions.storeLocation(unwrappedResponse));
    }
  },
});

// fetch turbines for location on location view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const locationId = navigateAction.payload.route.params.id;
    const response = await client.apiV1ManagementTechTurbinesList(
      undefined,
      undefined,
      locationId,
      undefined,
      undefined,
      undefined,
      ["client,location" as ApiV1ManagementTechTurbinesListSideloadEnum]
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.addTurbineList(unwrappedResponse));

      const sideload = {} as SideloadedTurbineInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(turbineActions.addTurbineSideload(sideload));
    }
  },
});

// clear location when navigating to create
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(locationActions.clearLocation());
  },
});

// add or update location
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: locationActions.addOrUpdateLocation,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementLocationsCreate(action.payload);
      const unwrapped = responseUnwrap<Location>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_location_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.LOCATION_LIST));
      }
    } else {
      const response = await client.apiV1ManagementLocationsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Location>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_location_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.LOCATION_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// delete location
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: locationActions.deleteLocation,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementLocationsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_location_delete_success",
        }),
      });
      const response = await client.apiV1ManagementLocationsList();
      const unwrapped = responseUnwrap(response);
      if (!("error" in unwrapped)) {
        dispatch(locationActions.addLocationList(unwrapped));
      }
    }
  },
});

// load user management list when navigating to user list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_USER_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const [response, groupsResponse] = await Promise.all([
      fetchUserList(navigateAction.payload.route.params),
      client.apiV1ManagementMetaGroupsList(),
    ]);
    const unwrapped = responseUnwrap<PaginatedUserList>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.addUserList(unwrapped));
    }
    const groupsUnwrapped = responseUnwrap<UserGroup[]>(groupsResponse);
    if (!("error" in groupsUnwrapped)) {
      dispatch(userManagementActions.storeGroups(groupsUnwrapped));
    }
  },
});

// fetch single user when navigating to edit page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_USER_EDIT),
  effect: async (action, { dispatch }) => {
    dispatch(userManagementActions.removeUser());
    const navigateAction = action as NavigateAction;
    const [response, groupsResponse] = await Promise.all([
      client.apiV1ManagementUserManagementRetrieve(
        navigateAction.payload.route.params.id
      ),
      client.apiV1ManagementMetaGroupsList(),
    ]);
    const unwrapped = responseUnwrap<User>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.storeUser(unwrapped));
    }
    const groupsUnwrapped = responseUnwrap<UserGroup[]>(groupsResponse);
    if (!("error" in groupsUnwrapped)) {
      dispatch(userManagementActions.storeGroups(groupsUnwrapped));
    }
  },
});

// clear user when navigating to create page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_USER_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(userManagementActions.removeUser());
    const groupsResponse = await client.apiV1ManagementMetaGroupsList();
    const groupsUnwrapped = responseUnwrap<UserGroup[]>(groupsResponse);
    if (!("error" in groupsUnwrapped)) {
      dispatch(userManagementActions.storeGroups(groupsUnwrapped));
    }
  },
});

// create or update user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.addOrUpdateUser,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementUserManagementCreate(action.payload);
      const unwrapped = responseUnwrap<User>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({ id: "notification_user_create_success" }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_USER_LIST));
      }
    } else {
      const response = await client.apiV1ManagementUserManagementPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<User>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({ id: "notification_user_update_success" }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_USER_LIST));
      }
    }
  },
});

// delete user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.deleteUser,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementUserManagementDestroy(
      action.payload
    );
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_user_delete_success" }),
      });
      const response = await fetchUserList(state.router.route?.params);
      const unwrapped = responseUnwrap<PaginatedUserList>(response);
      if (!("error" in unwrapped)) {
        dispatch(userManagementActions.addUserList(unwrapped));
      }
    }
  },
});

// deactivate user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.deactivateUser,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const currentRoute = state.router.route;
    await client.apiV1ManagementUserManagementDeactivateUserPartialUpdate(action.payload);
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_user_deactivate_success" }),
    });
    const response = await fetchUserList(currentRoute?.params);
    const unwrapped = responseUnwrap<PaginatedUserList>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.addUserList(unwrapped));
    }
  },
});

// reactivate user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.reactivateUser,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const currentRoute = state.router.route;
    await client.apiV1ManagementUserManagementReactivateUserPartialUpdate(action.payload);
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_user_reactivate_success" }),
    });
    const response = await fetchUserList(currentRoute?.params);
    const unwrapped = responseUnwrap<PaginatedUserList>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.addUserList(unwrapped));
    }
  },
});

// load materials list when navigating to materials list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_MATERIAL_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementMaterialsList(
      navigateAction.payload.route.params.category,
      navigateAction.payload.route.params.page,
      undefined,
      getPerPage(navigateAction.payload.route.params),
      undefined,
      navigateAction.payload.route.params.search
    );
    const unwrapped = responseUnwrap<PaginatedMaterialsList>(response);
    if (!("error" in unwrapped)) {
      dispatch(materialsActions.addMaterialList(unwrapped));
    }
  },
});

// fetch single material when navigating to edit page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_MATERIAL_EDIT),
  effect: async (action, { dispatch }) => {
    dispatch(materialsActions.removeMaterial());
    const navigateAction = action as NavigateAction;
    const response = await client.apiV1ManagementMaterialsRetrieve(
      navigateAction.payload.route.params.id
    );
    const unwrapped = responseUnwrap<Materials>(response);
    if (!("error" in unwrapped)) {
      dispatch(materialsActions.storeMaterial(unwrapped));
    }
  },
});

// clear material when navigating to create page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_MATERIAL_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(materialsActions.removeMaterial());
  },
});

// create or update material
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: materialsActions.addOrUpdateMaterial,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementMaterialsCreate(action.payload);
      const unwrapped = responseUnwrap<Materials>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_material_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_LIST));
      }
    } else {
      const response = await client.apiV1ManagementMaterialsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Materials>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_material_update_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_LIST));
      }
    }
  },
});

// delete material
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: materialsActions.deleteMaterial,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementMaterialsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_material_delete_success",
        }),
      });
      const response = await client.apiV1ManagementMaterialsList();
      const unwrapped = responseUnwrap<PaginatedMaterialsList>(response);
      if (!("error" in unwrapped)) {
        dispatch(materialsActions.addMaterialList(unwrapped));
      }
    }
  },
});

// load services list when navigating to services list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementServicesList(
      navigateAction.payload.route.params.page,
      undefined,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.search
    );
    const unwrapped = responseUnwrap<PaginatedServicesList>(response);
    if (!("error" in unwrapped)) {
      dispatch(servicesActions.addServiceList(unwrapped));
    }
  },
});

// fetch single service when navigating to edit or view page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_VIEW),
  effect: async (action, { dispatch, getState }) => {
    dispatch(servicesActions.removeService());
    const navigateAction = action as NavigateAction;
    const response = await client.apiV1ManagementServicesRetrieve(
      navigateAction.payload.route.params.id
    );
    const unwrapped = responseUnwrap<Services>(response);
    if (!("error" in unwrapped)) {
      dispatch(servicesActions.storeService(unwrapped));
    }

    const state = getState();
    if (
      state.materials.list.results === undefined ||
      state.materials.list.results.length === 0
    ) {
      const materialsResponse = await client.apiV1ManagementMaterialsList(
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedMaterials =
        responseUnwrap<PaginatedMaterialsList>(materialsResponse);
      if (!("error" in unwrappedMaterials)) {
        dispatch(materialsActions.addMaterialList(unwrappedMaterials));
      }
    }
  },
});

// clear service when navigating to create page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_CREATE),
  effect: async (_action, { dispatch, getState }) => {
    dispatch(servicesActions.removeService());

    const state = getState();
    if (
      state.materials.list.results === undefined ||
      state.materials.list.results.length === 0
    ) {
      const materialsResponse = await client.apiV1ManagementMaterialsList(
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedMaterials =
        responseUnwrap<PaginatedMaterialsList>(materialsResponse);
      if (!("error" in unwrappedMaterials)) {
        dispatch(materialsActions.addMaterialList(unwrappedMaterials));
      }
    }
  },
});

// create or update service
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: servicesActions.addOrUpdateService,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementServicesCreate(action.payload);
      const unwrapped = responseUnwrap<Services>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_service_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_SERVICE_LIST));
      }
    } else {
      const response = await client.apiV1ManagementServicesPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Services>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_service_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.SETTINGS_SERVICE_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// delete service
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: servicesActions.deleteService,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementServicesDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_service_delete_success",
        }),
      });
      const response = await client.apiV1ManagementServicesList();
      const unwrapped = responseUnwrap<PaginatedServicesList>(response);
      if (!("error" in unwrapped)) {
        dispatch(servicesActions.addServiceList(unwrapped));
      }
    }
  },
});

// creates tokens on login
// listener.startListening.withTypes<AppDispatch>()({
// 	actionCreator: tokenActions.doLogin,
// 	effect: async (action, { dispatch }) => {
// 		const loginResponse = await client.apiV1AuthLoginCreate({
// 			username: action.payload.username,
// 			password: action.payload.password,
// 			totp: action.payload.totp,
// 		});
// 		localStorage.setItem("access", loginResponse.data.access);
// 		localStorage.setItem("refresh", loginResponse.data.refresh);
// 		dispatch(tokenActions.addToken(loginResponse.data));

// 		dispatch(routerActions.navigateTo(routeNames.TICKET_LIST));
// 	},
// });

// toggle report_done on ticket view
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.setReportDone,
  effect: async (action, { dispatch }) => {
    const { id, value } = action.payload;
    const res = await client.apiV1ManagementTicketPartialUpdate(id, {
      report_done: value,
    });
    if (res.status === 200) {
      const ticketRes = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketRes);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// set rating on ticket view
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.setRating,
  effect: async (action, { dispatch }) => {
    const { id, value } = action.payload;
    const res = await client.apiV1ManagementTicketPartialUpdate(id, { rating: value });
    if (res.status === 200) {
      const ticketRes = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketRes);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// fetch user profile and groups when navigating to profile view or edit
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.USER_PROFILE_VIEW) ||
    isNavigateAction(action as NavigateAction, routeNames.USER_PROFILE_EDIT),
  effect: async (action, { dispatch }) => {
    dispatch(userActions.clearViewedProfile());
    const navigateAction = action as NavigateAction;
    const profileId = navigateAction.payload.route.params.id;
    if (!profileId) return;
    const [profileRes, groupsRes] = await Promise.all([
      client.apiV1ManagementUsersRetrieve(profileId),
      client.apiV1ManagementMetaGroupsList(),
    ]);
    const unwrappedProfile = responseUnwrap<UserProfile>(profileRes);
    if (!("error" in unwrappedProfile)) {
      dispatch(userActions.storeViewedProfile(unwrappedProfile));
    }
    const unwrappedGroups = responseUnwrap<UserGroup[]>(groupsRes);
    if (!("error" in unwrappedGroups)) {
      dispatch(userActions.storeGroups(unwrappedGroups));
    }
  },
});

// clears state on logout
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.USER_LOGOUT),
  effect: async (_action, { dispatch }) => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");

    // should be implemented using router
    window.location.replace("/login");
    dispatch(
      routerActions.navigateTo(routeNames.USER_LOGIN, undefined, {
        reload: true,
      })
    );
  },
});

// redirects to login when no tokens
// listener.startListening.withTypes<RootState, AppDispatch>()({
// 	predicate: (action) =>
// 		isNavigateAction(action as NavigateAction, routeNames.USER_CURRENT),
// 	effect: async (_actions, { dispatch, getState }) => {
// 		const state = getState();

// 	},
// });

// fetch project list when navigating to project list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const [projectResponse, clientResponse] = await Promise.all([
      client.apiV1ManagementProjectsList(
        navigateAction.payload.route.params.client,
        navigateAction.payload.route.params.page,
        navigateAction.payload.route.params.name,
        navigateAction.payload.route.params.sort,
        getPerPage(navigateAction.payload.route.params),
        navigateAction.payload.route.params.project_status,
        navigateAction.payload.route.params.search
      ),
      client.apiV1ManagementClientsList(
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      ),
    ]);

    const unwrappedProjects = responseUnwrap<PaginatedProjectList>(projectResponse);
    if (!("error" in unwrappedProjects)) {
      dispatch(projectActions.addProjectList(unwrappedProjects));
    }

    const unwrappedClients = responseUnwrap<PaginatedClientList>(clientResponse);
    if (!("error" in unwrappedClients)) {
      dispatch(projectActions.addClients(unwrappedClients.results));
    }
  },
});

// fetch project on edit or view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_VIEW),
  effect: async (action, { dispatch }) => {
    dispatch(projectActions.removeProject());
    const navigateAction = action as NavigateAction;
    const projectId = navigateAction.payload.route.params.id;
    if (!projectId) return;

    const projectResponse = await client.apiV1ManagementProjectsRetrieve(projectId);
    if (projectResponse) {
      const unwrapped = responseUnwrap<ProjectDetailed>(projectResponse);
      if (!("error" in unwrapped)) {
        dispatch(projectActions.storeProject(unwrapped));
      }
    }

    const clientResponse = await client.apiV1ManagementClientsList(
      undefined,
      undefined,
      undefined,
      UNPAGED_LIMIT
    );
    if (clientResponse) {
      const unwrappedClients = responseUnwrap<PaginatedClientList>(clientResponse);
      if (!("error" in unwrappedClients)) {
        dispatch(projectActions.addClients(unwrappedClients.results));
      }
    }

    if (isNavigateAction(action as NavigateAction, routeNames.PROJECT_VIEW)) {
      dispatch(ticketDataActions.fetchTicketData({}));
    }
  },
});

// clear project when navigating to create page, and load client list
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(projectActions.removeProject());
    const clientResponse = await client.apiV1ManagementClientsList(
      undefined,
      undefined,
      undefined,
      UNPAGED_LIMIT
    );
    const unwrappedClients = responseUnwrap<PaginatedClientList>(clientResponse);
    if (!("error" in unwrappedClients)) {
      dispatch(projectActions.addClients(unwrappedClients.results));
    }
  },
});

// create or update project
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.addOrUpdateProject,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementProjectsCreate(action.payload);
      const unwrapped = responseUnwrap<Project>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_project_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.PROJECT_LIST));
      }
    } else {
      const response = await client.apiV1ManagementProjectsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Project>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_project_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.PROJECT_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// delete project
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.deleteProject,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementProjectsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_project_delete_success",
        }),
      });

      const currentRoute = state.router.route;
      const isOnProjectList = currentRoute?.name === routeNames.PROJECT_LIST;
      const isOnProjectView =
        currentRoute?.name === routeNames.PROJECT_VIEW ||
        currentRoute?.name === routeNames.PROJECT_EDIT;

      if (isOnProjectView) {
        dispatch(routerActions.navigateTo(routeNames.PROJECT_LIST));
      } else if (isOnProjectList) {
        const response = await client.apiV1ManagementProjectsList(
          currentRoute?.params?.client,
          currentRoute?.params?.page,
          currentRoute?.params?.name,
          currentRoute?.params?.sort,
          getPerPage(currentRoute?.params ?? {}),
          currentRoute?.params?.project_status,
          currentRoute?.params?.search
        );
        const unwrapped = responseUnwrap<PaginatedProjectList>(response);
        if (!("error" in unwrapped)) {
          dispatch(projectActions.addProjectList(unwrapped));
        }
      }
    }
  },
});

// inline start/deadline edit from the project preview work order table: PATCH only
// the changed field, then refetch the project because its tickets are sideloaded on
// the project retrieve and are not part of the work order list slice
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.projectTicketDateEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { projectId, ticketId, field, value } = action.payload;

    const response = await client.apiV1ManagementTicketPartialUpdate(ticketId, {
      [field]: value,
    });
    const unwrapped = responseUnwrap<Ticket>(response);
    if ("error" in unwrapped) {
      handleErrorNotification(state, unwrapped);
      return;
    }

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_ticket_update_success" }),
    });
  },
});

// mark project as delayed: backend cascades ongoing tickets to STANDBY as a side
// effect of the project status PATCH; capture their pre-delay statuses here so
// resumeProject can restore them later
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.markProjectDelayed,
  effect: async (action, { dispatch, getState }) => {
    const projectId = action.payload;
    const state = getState();
    const project = state.project.project;
    const intl = state.intl.datetimeIntl;
    const infoFunc = state.appFeedback.notification?.info;

    const projectResponse = await client.apiV1ManagementProjectsPartialUpdate(projectId, {
      project_status: "DELAYED",
    });
    const unwrappedProject = responseUnwrap<Project>(projectResponse);
    if ("error" in unwrappedProject) return;

    const ticketsToStandby = (project?.tickets ?? []).filter(
      (t) => t.id && (t.status === "NEW" || t.status === "IN_PROGRESS")
    );

    dispatch(
      projectActions.storePreDelayStatuses(
        Object.fromEntries(ticketsToStandby.map((t) => [t.id!, t.status!]))
      )
    );

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_project_marked_delayed" }),
      showProgress: true,
      pauseOnHover: true,
    });
  },
});

// resume project: set project to ACTIVE, restore STANDBY tickets to their pre-delay status
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.resumeProject,
  effect: async (action, { dispatch, getState }) => {
    const projectId = action.payload;
    const state = getState();
    const project = state.project.project;
    const preDelayStatuses = state.project.preDelayStatuses;
    const intl = state.intl.datetimeIntl;
    const infoFunc = state.appFeedback.notification?.info;

    const projectResponse = await client.apiV1ManagementProjectsPartialUpdate(projectId, {
      project_status: "ACTIVE",
    });
    const unwrappedProject = responseUnwrap<Project>(projectResponse);
    if ("error" in unwrappedProject) return;

    const ticketsToResume = (project?.tickets ?? []).filter(
      (t) => t.id && t.status === "STANDBY"
    );
    await Promise.allSettled(
      ticketsToResume.map((t) =>
        silentClient.apiV1ManagementTicketPartialUpdate(t.id!, {
          status: (preDelayStatuses[t.id!] ?? "NEW") as "NEW" | "IN_PROGRESS",
        })
      )
    );

    dispatch(projectActions.clearPreDelayStatuses());

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_project_resumed" }),
      showProgress: true,
      pauseOnHover: true,
    });
  },
});

// update user profile
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userActions.updateProfile,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { profileId, payload } = action.payload;

    const res = await client.apiV1ManagementUsersUpdatePartialUpdate(profileId, payload);
    dispatch(userActions.setUpdateProfileLoading(false));

    const unwrapped = responseUnwrap<UserProfile>(res);
    if (!("error" in unwrapped)) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_profile_update_success" }),
      });
      dispatch(routerActions.navigateTo(routeNames.USER_PROFILE_VIEW, { id: profileId }));
    }
  },
});

// change password
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userActions.changePassword,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { profileId, oldPassword, newPassword } = action.payload;

    dispatch(userActions.setChangePasswordStatus("loading"));
    const res = await client.apiV1ManagementUsersChangePasswordCreate(profileId, {
      old: oldPassword,
      new: newPassword,
    });
    if (res.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_password_change_success" }),
      });
      dispatch(userActions.setChangePasswordStatus("succeeded"));
    } else {
      dispatch(userActions.setChangePasswordStatus("failed"));
    }
  },
});

// load dashboard data when navigating to dashboard
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) => isNavigateAction(action as NavigateAction, routeNames.DASHBOARD),
  effect: async (_action, { dispatch }) => {
    dispatch(dashboardActions.fetchDashboard());
    try {
      const [ticketsRes, projectsRes, clientsRes, usersRes, groupsRes] =
        await Promise.all([
          client.apiV1ManagementTicketList(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            UNPAGED_LIMIT,
            undefined,
            undefined,
            undefined,
            undefined,
            ["assignees,service" as ApiV1ManagementTicketListSideloadEnum],
            undefined
          ),
          client.apiV1ManagementProjectsList(
            undefined,
            undefined,
            undefined,
            undefined,
            UNPAGED_LIMIT
          ),
          client.apiV1ManagementClientsList(
            undefined,
            undefined,
            undefined,
            UNPAGED_LIMIT
          ),
          client.apiV1ManagementUserManagementList(
            undefined,
            undefined,
            undefined,
            true,
            undefined,
            UNPAGED_LIMIT
          ),
          client.apiV1ManagementMetaGroupsList(),
        ]);

      const ticketData = ticketsRes.data as unknown as PaginatedticketResultsSideloadList;
      const sideload = ticketData.results;
      const projData = projectsRes.data as unknown as { results?: Project[] };
      const clientData = clientsRes.data as unknown as { results?: Client[] };
      const usersData = usersRes.data as unknown as { results?: User[] };
      const groups = groupsRes.data as unknown as UserGroup[];
      const techGroupId = getTechnicianGroupId(groups);
      const allUsers = usersData.results ?? [];

      dispatch(
        dashboardActions.storeDashboard({
          tickets: (sideload.tickets as unknown as Ticket[]) ?? [],
          assigneeById: buildAssigneeById(
            (sideload.assignees as unknown as Parameters<typeof buildAssigneeById>[0]) ??
              []
          ),
          serviceById: buildServiceById(
            (sideload.service as unknown as Services[]) ?? []
          ),
          projects: projData.results ?? [],
          clientById: buildClientById(clientData.results ?? []),
          technicians:
            techGroupId !== null ? filterActiveTechnicians(allUsers, techGroupId) : [],
        })
      );
    } catch (err) {
      dispatch(
        dashboardActions.setDashboardError(
          err instanceof Error ? err.message : "Failed to load dashboard"
        )
      );
    }
  },
});

// load WO manager notifications when navigating to the notifications page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.NOTIFICATIONS),
  effect: async (_action, { dispatch }) => {
    dispatch(notificationActions.fetchNotifications());
    try {
      const [notificationsRes, techniciansRes] = await Promise.all([
        client.apiV1ManagementNotificationsList(
          undefined,
          undefined,
          "-created_at",
          UNPAGED_LIMIT
        ),
        client.apiV1ManagementUsersList(
          undefined,
          undefined,
          "technician",
          true,
          UNPAGED_LIMIT
        ),
      ]);

      const notificationsData = notificationsRes.data as unknown as {
        results?: NotificationList[];
      };
      const techniciansData = techniciansRes.data as unknown as {
        results?: UserProfile[];
      };

      dispatch(
        notificationActions.storeNotifications({
          list: notificationsData.results ?? [],
          technicianIds: (techniciansData.results ?? []).map((u) => u.id),
        })
      );
    } catch (err) {
      dispatch(
        notificationActions.setNotificationsError(
          err instanceof Error ? err.message : "Failed to load notifications"
        )
      );
    }
  },
});

// mark a group of WO manager notifications as read
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: notificationActions.requestMarkRead,
  effect: async (action, { dispatch }) => {
    dispatch(notificationActions.markNotificationsRead(action.payload));
    await Promise.all(
      action.payload.map((id) =>
        client.apiV1ManagementNotificationsMarkAsReadPartialUpdate(id)
      )
    );
  },
});

// Initialise router
export const routerInstance = createRouter(routes, {
  allowNotFound: false,
});
// biome-ignore lint/correctness/useHookAtTopLevel: This is not a hook
routerInstance.usePlugin(browserPluginFactory());

export function confStore() {
  return configureStore({
    reducer: {
      router: router5Reducer,
      ticket: ticketReducer,
      ticketData: ticketDataReducer,
      token: tokenReducer,
      userstate: userSliceReducer,
      client: clientReducer,
      turbine: turbineReducer,
      blade: bladeReducer,
      appFeedback: appFeedbackSlice.reducer,
      intl: intlSliceReducer,
      location: locationReducer,
      userManagement: userManagementReducer,
      materials: materialsReducer,
      services: servicesReducer,
      project: projectReducer,
      dashboard: dashboardReducer,
      notification: notificationReducer,
      serviceCounter: serviceCounterReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ["appFeedback/setAppFeedbackControls"],
          ignoredPaths: ["appFeedback", "intl"],
        },
      })
        .concat(router5Middleware(routerInstance))
        .prepend(listener.middleware),
  });
}
export const store = confStore();
routerInstance.start();
